<template>
  <cs-preloader-overlay :loading="loading">
    <div class="main-container">

      <div class="header-container">
        <responsive-controller>
          <template #tablet>
            <screen-header
              :history-mode="false"
              :title="$t('project.settings')"
              @navigate="$router.push({ name: 'cabinet' })"
            />
          </template>
          <template #default>
            <div>
              <navigate-button
                v-absolute
                v-left="-80"
                @navigate="$router.push({ name: 'cabinet' })"
              />
              <screen-title :title="$t('project.settings')" />
            </div>
          </template>
        </responsive-controller>
      </div>

      <div class="form-container">
        <div class="download-container">
          <attachment-image
            :link="attachment.link"
            input-id="project.attachment"
            @loading="onImageLoading"
            @uploaded="data => attachment.setData(data)"
            @reset="attachment.reset()"
          />
        </div>

        <substrate-mount>
          <div class="input-container">
            <input-text
              ref="name"
              v-model="name"
              input-id="input-email"
              :input-name="$t('common.name')"
              :input-placeholder="$t('enter.title.or.name')"
              :max-length="60"
              :min-length="3"
              :readonly="!projectValidated"
            />
          </div>
          <div class="input-container">
            <input-textarea
              ref="inp"
              v-model="description"
              input-id="input-description"
              :input-name="$t('project.description')"
              :input-title="$t('enter.description')"
              :input-placeholder="$t('project.description')"
              :max-length="255"
              :rows="1"
            />
          </div>
          <div class="input-container">
            <input-link
              ref="link"
              v-model="link"
              input-id="project-link"
              :input-name="$t('link.to.project')"
              :input-placeholder="$t('indicate.link.to.project')"
              readonly
            />
          </div>
          <div class="input-container">
            <input-id
              ref="code"
              v-model="code"
              input-id="project-index"
              :input-name="$t('project.id')"
              :input-placeholder="$t('indicate.project.id')"
            />
          </div>
          <div class="input-container">
            <input-text
              ref="apiKey"
              v-model="apiKey"
              input-id="merchant-apiKey"
              :input-name="$t('token.for.api')"
              :input-placeholder="$t('token.for.api')"
              data-private
              disabled
              readonly
            />
          </div>
          <div v-if="currency" class="option-container" @click="handleShownCurrenciesList">
            <option-panel :option="currency" noactive>
              <div slot="icon" :class="$style.iconCurrency">
                {{ currency.sign }}
              </div>
              <cs-button v-if="isChangeCurrencyPermission" type="secondary">
                {{ $t('common.change') }}
              </cs-button>
            </option-panel>
          </div>
          <div v-if="localization" class="option-container" @click="handleShownLocalizationList">
            <option-panel :option="localization" noactive>
              <div slot="icon" :class="$style.iconLocalization">
                <country-flag :language-code="localization.code" />
              </div>
              <cs-button type="secondary">
                {{ $t('common.change') }}
              </cs-button>
            </option-panel>
          </div>
        </substrate-mount>

        <div class="button-primary-container">
          <cs-button :width="300" :disabled="!isValid" @click="onSubmit">
            {{ $t('common.save') }}
          </cs-button>
        </div>
      </div>

      <cs-dialog v-model="isShownCurrencies">
        <dialog-list-layout>
          <option-panel
            v-for="currency in getPreparedCurrencies"
            :key="currency.id"
            :option="currency"
            @click="handleChangeCurrency"
          >
            <div slot="icon" :class="$style.iconCurrency">
              {{ currency.sign }}
            </div>
          </option-panel>
        </dialog-list-layout>
      </cs-dialog>

      <cs-dialog v-model="isShownCurrencyError">
        <dialog-default-layout>
          <h2 slot="title">{{ $t('change.of.currency') }}</h2>
          <p slot="content" class="dialog-text">
            {{ $t('change.of.currency.error') }}
          </p>
          <div slot="footer" class="confirmation-control-buttons">
            <div class="confirmation-control-active">
              <cs-button width="100%" @click="handlePayoutRedirect">
                {{ $t('to.payout') }}
              </cs-button>
            </div>
            <div class="confirmation-control-cancel">
              <cs-button width="100%" type="transparent" @click="isShownCurrencyError = false">
                {{ $t('common.cancel') }}
              </cs-button>
            </div>
          </div>
        </dialog-default-layout>
      </cs-dialog>

      <languages-list-modal
        v-model="isShownLocalizationList"
        :languages="formattedLanguages"
        @change-localization="handleChangeLocalization"
      />
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import HTTPService from '@/services/Http';
import { fetch as getCurrencies } from 'services/api/currency';
import { getLanguages } from 'services/localization';

import attachmentMixin from 'lib/mixins/attachmentMixin';

import ScreenTitle from 'molecules/screen/ScreenTitle';
import ScreenHeader from 'molecules/screen/ScreenHeader';
import NavigateButton from 'molecules/common/NavigateButton';

import SubstrateMount from '@/components/capusta/SubstrateMount';
import InputText from '@/components/inputElements/InputText';
import InputTextarea from '@/components/inputElements/InputTextarea';
import InputId from '@/components/inputElements/InputId';
import InputLink from '@/components/inputElements/InputLink';

import OptionPanel from '@/components/capusta/OptionPanel';
import AttachmentImage from 'modules/AttachmentImage';

import DialogDefaultLayout from 'modules/Dialogs/DefaultLayout';
import DialogListLayout from 'modules/Dialogs/ListLayout';

import CountryFlag from 'molecules/localization/CountryFlag';
import LanguagesListModal from 'organisms/localization/LanguagesListModal';

import Attachment from 'lib/project/classes/Attachment';

export default {
  name: 'EditProject',
  mixins: [
    attachmentMixin,
  ],
  components: {
    ScreenTitle,
    ScreenHeader,
    NavigateButton,
    SubstrateMount,
    InputText,
    InputTextarea,
    InputId,
    InputLink,
    OptionPanel,
    AttachmentImage,
    DialogDefaultLayout,
    DialogListLayout,
    CountryFlag,
    LanguagesListModal,
  },
  created() {
    this.loadCurrencies();
    this.loadLanguages();
  },
  mounted() {
    this.name = this.PROJECT_INFO.name;
    this.email = this.PROJECT_INFO.email;
    this.phone = this.PROJECT_INFO.phone;
    this.code = this.PROJECT_INFO.code;
    this.link = this.PROJECT_INFO.link;
    this.apiKey = this.MERCHANT_INFO.apiKey;
    this.description = this.PROJECT_INFO.description;

    if (this.PROJECT_INFO.attachment) {
      this.attachment = new Attachment(this.PROJECT_INFO.attachment);
    }
  },
  data() {
    return {
      name: '',
      code: '',
      link: '',
      apiKey: '',
      description: '',
      attachment: new Attachment,
      loading: false,

      currencies: [],
      isShownCurrencies: false,
      isShownCurrencyError: false,
      languagesList: [],
      isShownLocalizationList: false,
    };
  },
  computed: {
    isValid() {
      if (this.submittingDisabled) {
        return false;
      }

      return this.name && !this.$refs.name.$v.$error && !this.$refs.code.$v.$error && !this.$refs.link.$v.$error && !this.$refs.inp.$v.$error;
    },
    currency() {
      const { currency } = this.PROJECT_INFO;

      if (this.$validate.isNull(currency)) {
        return null;
      }

      return {
        label: this.$t('project.currency'),
        description: this.$t(currency.name),
        sign: currency.sign,
      };
    },
    getPreparedCurrencies() {
      const { currency } = this.PROJECT_INFO;

      return this.currencies.map(item => {
        const isCurrent = item.id === currency.id;

        return {
          ...item,
          label: isCurrent ? this.$t('project.currency') : this.$t('can.choose'),
          description: this.$t(item.name),
          active: isCurrent,
        };
      });
    },
    localization() {
      const { localization } = this.PROJECT_INFO;

      if (this.$validate.isNull(localization)) {
        return {
          code: 'UNKNOWN',
          description: this.$t('localization.default.description'),
          label: this.$t('localization.default.title'),
        };
      }

      return {
        ...localization,
        description: localization.title,
        label: this.$t('settings.payment.page.localization'),
      };
    },
    formattedLanguages() {
      return this.languagesList.map(language => {
        return {
          ...language,
          active: language.code === this.localization.code,
          description: this.$t(language.description),
        };
      });
    },
    buttonWidth() {
      return this.isDesktop ? 'auto' : '100%';
    },
    ...mapState(['PROJECT_INFO', 'MERCHANT_INFO']),
    ...mapGetters(['isChangeCurrencyPermission', 'isDesktop', 'projectValidated']),
  },
  methods: {
    async loadCurrencies() {
      try {
        const { data } = await getCurrencies();
        this.currencies = data;
      } catch (error) {
        console.log(error);
      }
    },
    async loadLanguages() {
      try {
        const languages = await getLanguages({ type: 'payment_page' });

        languages.push({
          code: 'UNKNOWN',
          description: this.$t('localization.default.description'),
          title: this.$t('localization.default.title'),
        });

        this.languagesList = languages;
      } catch (error) {
        console.error(error);
      }
    },
    async onSubmit() {
      if (!this.isValid) {
        this.$alert({
          type: 'warning',
          text: this.$t('fill.fields.correctly'),
        });
        return;
      }

      this.loading = true;

      const requestData = {
        name: this.name,
        code: this.code,
        description: this.description,
        attachment: this.attachment.getData(),
        currency: this.PROJECT_INFO.currency,
        localization: this.PROJECT_INFO.localization,
      };

      try {
        await HTTPService.editProject(requestData, this.PROJECT_INFO.projectId);

        this.$alert({
          type: 'success',
          text: this.$t('project.settings.saved'),
        });

        this.$router.push({ name: 'cabinet' });
      } catch (error) {
        console.log(error);
        const { data, status } = error;

        if (status === 406) {
          this.$alert({
            type: 'warning',
            text: this.$t('project.id.exists'),
          });
          return;
        }

        if (data.status === 'CURRENCY_ERROR') {
          this.isShownCurrencyError = true;
        }

        this.$alert({
          type: 'warning',
          text: this.$t('project.settings.not.saved'),
        });
      }

      this.loading = false;
    },
    handleChangeCurrency(data) {
      if (this.PROJECT_INFO.balance > 0) {
        this.isShownCurrencies = false;

        this.$nextTick(() => {
          this.isShownCurrencyError = true;
        });

        return;
      }

      const currency = {
        code: data.code,
        digital_code: data.digital_code,
        id: data.id,
        internal: data.internal,
        name: data.name,
        sign: data.sign,
      };

      this.PROJECT_INFO.currency = currency;

      this.$nextTick(() => {
        this.isShownCurrencies = false;
      });
    },
    handleShownCurrenciesList() {
      if (!this.isChangeCurrencyPermission) {
        return;
      }

      this.isShownCurrencies = true;
    },
    handlePayoutRedirect() {
      this.isShownCurrencyError = false;

      this.$nextTick(() => {
        this.$router.push({ name: 'payout' });
      });
    },
    handleShownLocalizationList() {
      this.isShownLocalizationList = true;
    },
    handleChangeLocalization(language) {
      if (this.$validate.isUndefined(language.id)) {
        this.PROJECT_INFO.localization = null;
        return;
      }

      this.PROJECT_INFO.localization = {
        code: language.code,
        description: language.description,
        id: language.id,
        title: language.title,
      };
    },
  },
};
</script>

<style lang="sass" module>
  .iconCurrency
    +currency-icon
    margin-right: 20px

  .iconLocalization
    width: 40px
    height: 40px
    display: flex
    align-items: center
    justify-content: center
    margin-right: 20px
    font-weight: 600
    font-size: 20px
    line-height: 24px
    color: #1E1D2F
    background-color: #181722
    border-radius: 50%
</style>

<style lang="sass" scoped>
  .main-container
    width: 100%
    max-width: 500px
    margin: 0 auto
    padding: 20px 0 60px

    +respond-to('medium')
      padding: 60px 0

  .header-container
    position: relative
    margin-bottom: 20px
    padding: 0 20px

    +respond-to('small')
      padding: 0

  .form-container
    width: 100%
    max-width: 500px
    position: relative
    margin: 0 auto

  .download-container
    position: absolute
    top: -70px
    right: 20px

    +respond-to('small')
      right: 0

    +respond-to('medium')
      right: -100px
      top: -100px

  .input-container
    &:not(:last-child)
      margin-bottom: 20px

    .input,
    .textarea
      margin: 0

  .button-primary-container
    display: flex
    justify-content: center
    margin-top: 40px

  .dialog-text
    font-weight: 500
    font-size: 14px
    line-height: 20px
    color: rgba(255,255,255,.75)

  .confirmation-control-buttons
    display: flex
    flex-direction: column

    +respond-to('small')
      flex-direction: row
      justify-content: space-between

  .confirmation-control-active
    width: 100%
    margin-bottom: 10px

    +respond-to('small')
      width: 190px
      margin-bottom: 0

  .confirmation-control-cancel
    width: 100%

    +respond-to('small')
      width: 190px
</style>
